import { FormProvider, useForm } from 'react-hook-form';

import { Divider, Grid, Stack } from '@mui/material';

import { ICustodyActionReq } from '@/api';
import { RenderFormField } from '@/components';
import { ConfirmTypes, useToastStore } from '@/store';
import { useUserStore } from '@/store/store.user';

import { CustodyActionSection } from './CustodyActionSection';
import { RelaseToOwnerFormFields } from './data';
import { PropertyModalFooter } from './PropertyModalFooter';
import { SideModalTitle } from './SideModalTitle';
import {
  getCustodyActionDefaultValues,
  normalizeCustodyActionDateTime,
  useCustodyOfficerOptions,
} from './useCustodyActionForm';
import { useKasePropertyContext } from '../../PropertyContext';

export const ToOwnerForm = () => {
  const { updateToast } = useToastStore();
  const {
    currentPropertyId,
    parentId,
    sourceAdapter,
    custodyModalValues: { propertyOwnerName },
    custodyModalValues,
  } = useKasePropertyContext();
  const { bulkSelection } = custodyModalValues;
  const { user } = useUserStore();
  const { officerOptions } = useCustodyOfficerOptions();

  const methods = useForm<ICustodyActionReq & { owner: string }>({
    defaultValues: {
      notes: '',
      owner: propertyOwnerName,
      ...getCustodyActionDefaultValues(user),
    },
    mode: 'all',
  });
  const { getValues, trigger } = methods;

  const toOwnerErrorMessages = [
    'ownership was not specified for this property',
    'ownership was not provided for all selected properties',
  ];

  const onReleaseToOwner = async () => {
    // If bulkSelection is not undefined, it means user wants to Release to Owner multiple selected Properties
    const properties =
      bulkSelection ?? (currentPropertyId ? [currentPropertyId] : []);
    const isValid = await trigger();
    if (isValid && parentId && properties) {
      const values = getValues();
      values.actionDateTime = normalizeCustodyActionDateTime(
        values.actionDateTime,
      ) as string;
      const { owner, ...payload } = values;
      try {
        await sourceAdapter.custodyReleaseToOwner(
          parentId,
          properties,
          payload,
        );
        updateToast({
          open: true,
          type: ConfirmTypes.SUCCESS,
          message: 'Property is released to owner successfully.',
        });
        return true;
      } catch (err: any) {
        updateToast({
          open: true,
          message: err.message,
          type: toOwnerErrorMessages.includes(err.message.toLowerCase())
            ? ConfirmTypes.WARNING
            : ConfirmTypes.ERROR,
        });
      }
    }
    return false;
  };

  const shouldHideField = (fieldName: string) => {
    switch (fieldName) {
      case 'owner':
        // Hide owner field if bulk actions is applied
        return !!bulkSelection;

      default:
        return false;
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack>
        <SideModalTitle>To Owner</SideModalTitle>
        <Grid container>
          <Grid item md={12}>
            <CustodyActionSection officerOptions={officerOptions} />
          </Grid>
          {RelaseToOwnerFormFields.map(({ name, ...rest }) => (
            <Grid key={name} item md={12}>
              {name === 'notes' && !bulkSelection && (
                <Divider sx={{ margin: '10px 0 20px' }} />
              )}

              {shouldHideField(name) ? null : (
                <RenderFormField {...rest} name={name} />
              )}
            </Grid>
          ))}
        </Grid>
        <PropertyModalFooter onSave={onReleaseToOwner} />
      </Stack>
    </FormProvider>
  );
};
